/** TODOs
 *  - SRP - Query flow needs to work for Carequality and TEFCA individually. Consider an interface and/or a strategy
 *  - Load saved query shouldn't assume governance. There's likely a table that needs updating.
 */

import { computed, inject, Injectable, linkedSignal, signal, untracked } from "@angular/core";
import { FormControl, FormGroup, Validators } from "@angular/forms";
import { Router } from "@angular/router";
import { requiredWithMessageValidator } from "@kno2/common/validators";
import { SessionService } from "@kno2/core/session";
import { DirectoryClientV2, Governance } from "@kno2/data-access/directory-api";
import {
    QueryApiService,
    QueryDocumentMetadata,
    QueryDocumentQueryRequest,
    QueryPatientIdentifier,
    QueryPatientQueryResponse,
    QueryRequester
} from "@kno2/data-access/query-api";
import { QueryHistoryService, QueryOrganization, RequestHistoryDocumentQuery } from "@kno2/data-access/query-history";
import { FormService } from "@kno2/shared/forms";
import { renameProperty, tryParseDate } from "@kno2/utils";
import { format, isDate } from "date-fns";
import { catchError, firstValueFrom, map, Observable, of, shareReplay } from "rxjs";

const DIRECTORY_FETCH_FAILED = "Failed to load directory entry";

/** The subset of a directory entry the query flow needs: requester identifiers (npi/naic) and the org's purpose-of-use codes. */
export interface RequesterDirectoryEntry {
    npi?: string;
    naic?: string;
    purposeOfUseCodes?: string[];
}

@Injectable({
    providedIn: "root"
})
export class QueryService {
    private readonly router = inject(Router);
    private readonly queryApiService = inject(QueryApiService);
    private readonly queryHistoryService = inject(QueryHistoryService);
    private readonly sessionService = inject(SessionService);
    private readonly formService = inject(FormService);
    private readonly directoryClient = inject(DirectoryClientV2);

    private error = signal<{
        title: string;
        messages: string[];
    }>({
        title: "",
        messages: []
    });
    private setError = (title: string, messages: string[] = []) => this.error.set({ title, messages });
    public clearError = () => this.error.set({ title: "", messages: [] });
    public getError = computed(() => ({
        title: this.error().title,
        messages: this.error().messages
    }));
    public hasError = computed(() => !!this.error().title || this.error().messages.length);

    public isInitiator = signal(false);

    public readonly governance = signal<Governance>(Governance.Carequality);
    public readonly isTefca = computed(() => this.governance() === Governance.Tefca);

    private query = signal<RequestHistoryDocumentQuery>({} as RequestHistoryDocumentQuery);
    public getorganizationQuery = computed(() => this.query().organizationQuery);
    public getSelectedOrganizations = computed(() => this.query().selectedOrganizations);
    public getPatientQuery = computed(() => this.query().patientQuery);
    public resumeQuery = (query: RequestHistoryDocumentQuery) => {
        this.governance.set(query.governance);
        this.query.set(query);
    };
    public resetQuery = () => this.query.set({} as RequestHistoryDocumentQuery);

    public form = linkedSignal<RequestHistoryDocumentQuery | null, FormGroup>({
        source: this.query,
        computation: (query, previous) => this.formComputation(query, previous?.value)
    });
    public resetForm = () => this.form.set(this.formComputation(null));

    public organizations = signal<QueryOrganization[]>([]);
    public patientResponse = signal<QueryPatientQueryResponse | null>(null);

    private readonly directoryEntries = new Map<Governance, Observable<RequesterDirectoryEntry | null>>();

    public getDirectoryEntry(governance: Governance): Observable<RequesterDirectoryEntry | null> {
        const hit = this.directoryEntries.get(governance);
        if (hit) return hit;
        const orgId = this.sessionService.profileData()?.kno2OrganizationId;
        const stream = (
            orgId == null
                ? of(null as RequesterDirectoryEntry | null)
                : this.directoryClient.directoryEntrySearch(true, { kno2OrganizationId: orgId, governance }).pipe(
                      map((results) => {
                          const entry = results?.[0];
                          return entry ? { npi: entry.npi, naic: entry.naic, purposeOfUseCodes: entry.initiatorPurposeOfUseCodes } : null;
                      })
                  )
        ).pipe(
            catchError(() => of(null as RequesterDirectoryEntry | null)),
            shareReplay({ bufferSize: 1, refCount: false })
        );
        this.directoryEntries.set(governance, stream);
        return stream;
    }

    public validateProfile(): void {
        const profile = this.sessionService.profileData();
        if (!profile?.job?.roleCode)
            this.setError("Primary Job Function is not set", ["Please ask an administrator to set the Primary Job Function for your user."]);
    }

    public goTo(route: string): void {
        this.clearError();
        this.router.navigate([route], {
            queryParamsHandling: "preserve"
        });
    }

    public validateBroadSearch(): void {
        this.clearError();
        if (this.form().get("purposeOfUseCode")!.invalid) {
            this.setError("There was an error querying targets. Please include the following information.", ["Purpose of Use is required."]);
            return;
        }

        const formValue = this.form().getRawValue();
        this.query.update((previousQuery) => ({ ...previousQuery, ...formValue, governance: this.governance() }));
    }

    public async searchOrganizations(): Promise<void> {
        this.clearError();
        const errors: string[] = [];

        const form = this.form().get("organizationQuery") as FormGroup;
        form.markAllAsTouched();
        const hasAnyValue = Object.values(this.form().get("organizationQuery")?.value).some((value) => value);
        if (!hasAnyValue) {
            errors.push("At least one value is required to search.");
        }

        if (this.form().get("purposeOfUseCode")!.invalid) {
            errors.push("Purpose of Use is required.");
        }

        const formErrors = this.formService.getErrorValues<string>(form);
        if (formErrors.length) {
            errors.push(...formErrors);
        }

        if (errors.length) {
            this.setError("There was an error querying organizations. Please include the following information.", errors);
            return;
        }

        const formValue = this.form().getRawValue();

        try {
            const results = await this.queryApiService.searchOrganizations({
                ...renameProperty(formValue.organizationQuery, "zipcode", "zipCode"),
                governance: this.governance()
            });
            this.organizations.set(results);
            const query = {
                ...this.query(),
                ...formValue,
                governance: this.governance()
            };
            const queryId = await this.queryHistoryService.saveQuery(query);

            this.query.update((previousQuery) => ({
                ...previousQuery,
                ...query,
                id: previousQuery?.id ?? queryId?.id
            }));
        } catch {
            this.setError("There was an error querying organizations. Please try again.");
        }
    }

    public async selectOrganizations(organizations: QueryOrganization[]): Promise<void> {
        this.form().get("selectedOrganizations")!.setValue(organizations);

        this.query.update((query) => ({
            ...query,
            selectedOrganizations: organizations
        }));

        await this.queryHistoryService.saveQuery(this.query());
    }

    public async searchPatients(): Promise<void> {
        this.clearError();
        const form = this.form().get("patientQuery") as FormGroup;
        form.markAllAsTouched();

        const errors = this.formService.getErrorValues<string>(form);
        if (errors.length) {
            this.setError("There was an error querying patients. Please include the following information.", errors);
            return;
        }

        const formValue = form.getRawValue();
        const organizationIds = this.form()
            .get("selectedOrganizations")!
            .value.map((organization: QueryOrganization) => organization.orgId);

        try {
            const requester = {
                ...(await this.buildRequester()),
                organizationName: this.sessionService.profileData()!.organizationName
            };
            const payload = {
                ...formValue,
                birthDate: format(formValue.birthdate, "MMddyyyy"),
                organizationIds,
                requester,
                governance: this.governance()
            };

            if (this.isTefca()) payload.queryType = "broad";

            const results = await this.queryApiService.queryPatientsByFacility(payload);
            this.patientResponse.set(results);
            this.query.update((query) => ({
                ...query,
                governance: this.governance(),
                patientQuery: {
                    ...formValue,
                    birthdate: format(formValue.birthdate, "yyyy-MM-dd")
                }
            }));
            await this.queryHistoryService.saveQuery(this.query());
        } catch (e) {
            this.setError(this.errorMessage(e, "There was an error querying patients. Please try again."));
        }
    }

    public async searchDocuments(
        patientIdentifier: QueryPatientIdentifier,
        organizationId: string,
        serviceStartFrom: Date,
        serviceStopTo: Date
    ): Promise<QueryDocumentMetadata[]> {
        const { patientId, patientIdRoot } = patientIdentifier;
        if (!patientId || !patientIdRoot) {
            throw new Error("patientIdentifier is missing patientId or patientIdRoot");
        }
        const requester = await this.buildRequester();

        const payload: QueryDocumentQueryRequest = {
            homeCommunityId: patientIdRoot,
            organizationId,
            patientIdentifier: patientId,
            requester,
            suppressRawMeta: true,
            governance: this.governance()
        };

        if (this.isTefca()) {
            payload.serviceStartTimeTo = format(serviceStopTo, "yyyy-MM-dd'T'HH:mm:ss'+00:00'");
            payload.serviceStopTimeFrom = format(serviceStartFrom, "yyyy-MM-dd'T'HH:mm:ss'+00:00'");
        }

        return await this.queryApiService.queryDocuments(payload);
    }

    public async retrieveDocument(document: QueryDocumentMetadata, organizationId: string): Promise<string> {
        const requester = await this.buildRequester();
        const { documentUniqueId, homeCommunityId, repositoryUniqueId } = document;
        const payload = {
            governance: this.governance(),
            documentUniqueId,
            repositoryUniqueId,
            homeCommunityId,
            organizationId,
            requester
        };

        const [base64Xml] = await this.queryApiService.retrieveDocument(payload);
        if (!base64Xml) {
            throw new Error("Retrieve returned no document content");
        }
        const bytes = Uint8Array.from(atob(base64Xml), (c) => c.charCodeAt(0));
        return new TextDecoder("utf-8").decode(bytes);
    }

    public async sendDocumentToIntake(document: QueryDocumentMetadata, organizationId: string, sendTo: string): Promise<{ id: string }> {
        const requester = await this.buildRequester();
        const { documentUniqueId, homeCommunityId, repositoryUniqueId, mimeType } = document;

        const payload = {
            governance: this.governance(),
            documentUniqueId,
            repositoryUniqueId,
            homeCommunityId,
            organizationId,
            requester,
            fileName: document.documentName.split(" ").join("_"),
            mimeType,
            sendTo
        };

        return await this.queryApiService.sendToIntake(payload);
    }

    private async buildRequester(): Promise<QueryRequester> {
        const profile = this.sessionService.profileData()!;
        const base: QueryRequester = {
            roleCode: profile.job.roleCode,
            purposeOfUseCode: this.form().get("purposeOfUseCode")?.value as string
        };
        if (!this.isTefca()) return base;

        const entry = await firstValueFrom(this.getDirectoryEntry(this.governance()));
        if (!entry) {
            throw new Error(DIRECTORY_FETCH_FAILED);
        }
        return {
            ...base,
            id: profile.userName,
            fullName: profile.fullName,
            npi: entry.npi,
            naic: entry.naic
        };
    }

    private errorMessage(error: unknown, fallback: string): string {
        if (error instanceof Error && error.message === DIRECTORY_FETCH_FAILED) {
            return "Failed to load directory entry.";
        }
        return fallback;
    }

    private normalizeBirthdate(value: unknown): Date | null {
        if (isDate(value)) return value as Date;
        return tryParseDate(value as string);
    }

    private formComputation(query?: RequestHistoryDocumentQuery | null, currentForm?: FormGroup): FormGroup {
        const { purposeOfUseCode, organizationQuery, patientQuery, selectedOrganizations } = query || {};
        const form =
            currentForm ||
            new FormGroup({
                purposeOfUseCode: new FormControl(purposeOfUseCode, Validators.required),
                organizationQuery: new FormGroup({
                    name: new FormControl(organizationQuery?.name),
                    isHealthReferralRegionSearch: new FormControl(organizationQuery?.isHealthReferralRegionSearch ?? false),
                    address: new FormControl(organizationQuery?.address),
                    city: new FormControl(organizationQuery?.city),
                    state: new FormControl(organizationQuery?.state),
                    zipcode: new FormControl(organizationQuery?.zipcode)
                }),
                patientQuery: new FormGroup({
                    firstName: new FormControl(patientQuery?.firstName, requiredWithMessageValidator("First Name is required.")),
                    middleName: new FormControl(patientQuery?.middleName),
                    lastName: new FormControl(patientQuery?.lastName, requiredWithMessageValidator("Last Name is required.")),
                    birthdate: new FormControl(this.normalizeBirthdate(patientQuery?.birthdate), requiredWithMessageValidator("Date of Birth is required.")),
                    gender: new FormControl(patientQuery?.gender, requiredWithMessageValidator("Gender is required.")),
                    ssn: new FormControl(patientQuery?.ssn),
                    street: new FormControl(patientQuery?.street),
                    street2: new FormControl(patientQuery?.street2),
                    city: new FormControl(patientQuery?.city),
                    state: new FormControl(patientQuery?.state),
                    zipcode: new FormControl(patientQuery?.zipcode),
                    phoneNumber: new FormControl(patientQuery?.phoneNumber)
                }),
                selectedOrganizations: new FormControl([...(selectedOrganizations || [])])
            });

        untracked(() =>
            form.patchValue({
                purposeOfUseCode: purposeOfUseCode,
                organizationQuery: {
                    ...organizationQuery,
                    isHealthReferralRegionSearch: organizationQuery?.isHealthReferralRegionSearch ?? false
                },
                patientQuery: {
                    ...patientQuery,
                    birthdate: this.normalizeBirthdate(patientQuery?.birthdate)
                },
                selectedOrganizations: selectedOrganizations || []
            })
        );

        return form;
    }
}
